<template>
  <div class="blog-page">
    <div class="container py-4">
      <SectionHeader label="المدونة" title="أحدث المقالات" subtitle="نصائح ومقالات تعليمية لمساعدتك على التفوق الدراسي" />

      <!-- Controls -->
      <div class="blog-controls">
        <div class="blog-search">
          <i class="pi pi-search"></i>
          <input type="text" v-model="search" placeholder="ابحث في المقالات..." @keyup.enter="applySearch" />
          <button v-if="search" class="blog-search__clear" @click="clearSearch" aria-label="مسح"><i class="pi pi-times"></i></button>
        </div>
        <select class="blog-filter" v-model="category" @change="reload">
          <option value="">كل الأقسام</option>
          <option v-for="c in categories" :key="c.id" :value="c.slug">{{ c.title }}</option>
        </select>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="blog-grid">
        <CardSkeleton v-for="n in 6" :key="n" />
      </div>

      <!-- Error -->
      <div v-else-if="error" class="blog-state">
        <i class="pi pi-exclamation-triangle"></i>
        <p>تعذّر تحميل المقالات.</p>
        <button class="blog-retry" @click="load"><i class="pi pi-refresh"></i> إعادة المحاولة</button>
      </div>

      <!-- Empty -->
      <div v-else-if="!posts.length" class="blog-state">
        <i class="pi pi-inbox"></i>
        <p>{{ search ? 'لا توجد نتائج مطابقة للبحث.' : 'لا توجد مقالات منشورة حالياً.' }}</p>
      </div>

      <!-- Grid -->
      <template v-else>
        <div class="blog-grid">
          <BlogCard v-for="post in posts" :key="post.id" :post="post" />
        </div>

        <!-- Pagination (server-side) -->
        <div v-if="totalPages > 1" class="blog-pager">
          <button :disabled="page === 1" @click="goToPage(page - 1)"><i class="pi pi-angle-right"></i></button>
          <button v-for="p in totalPages" :key="p" :class="{ active: p === page }" @click="goToPage(p)">{{ p }}</button>
          <button :disabled="page === totalPages" @click="goToPage(page + 1)"><i class="pi pi-angle-left"></i></button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader.vue';
import BlogCard from '@/components/blog/BlogCard.vue';
import CardSkeleton from '@/components/subscriptions/CardSkeleton.vue';
import { blogService } from '@/services/blogService.js';
import { applySeoMeta } from '@/composables/useSeoMeta';

export default {
  name: 'BlogListing',
  components: { SectionHeader, BlogCard, CardSkeleton },
  data() {
    return {
      posts: [],
      categories: [],
      loading: true,
      error: false,
      search: '',
      category: '',
      page: 1,
      totalPages: 1,
    };
  },
  methods: {
    async loadCategories() {
      try {
        this.categories = await blogService.categories();
      } catch { /* non-fatal */ }
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const result = await blogService.posts({
          page: this.page,
          category: this.category,
          search: this.search,
        });
        this.posts = result.data ?? [];
        this.totalPages = result.last_page ?? 1;
      } catch {
        this.error = true;
        this.posts = [];
      } finally {
        this.loading = false;
      }
    },
    reload() {
      this.page = 1;
      this.load();
    },
    applySearch() {
      this.reload();
    },
    clearSearch() {
      this.search = '';
      this.reload();
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.load();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  },
  mounted() {
    this.loadCategories();
    this.load();
    this._seoCleanup = applySeoMeta({
      title: 'المدونة | أ/ طارق عبدالغني عبدالباري - مدرس الرياضيات',
      description: 'مقالات ونصائح تعليمية في مادة الرياضيات لطلاب المرحلتين الإعدادية والثانوية.',
      canonical: 'https://tarekmath.com/blogs',
      type: 'website',
    });
  },
  beforeUnmount() {
    if (this._seoCleanup) this._seoCleanup();
  },
};
</script>

<style scoped>
.blog-page { min-height: 60vh; padding-bottom: 5rem; }
.blog-controls {
  display: flex; gap: .75rem; flex-wrap: wrap; margin-bottom: 1.5rem;
}
.blog-search {
  position: relative; flex: 1; min-width: 200px;
  display: flex; align-items: center;
}
.blog-search > .pi-search {
  position: absolute; inset-inline-start: 14px; color: #94a3b8; font-size: .9rem;
}
.blog-search input {
  width: 100%; padding: .7rem 2.4rem; border: 1px solid #E5E7EB; border-radius: 12px;
  font-size: .9rem; outline: none; transition: border-color .2s;
}
.blog-search input:focus { border-color: #5B5FEF; }
.blog-search__clear {
  position: absolute; inset-inline-end: 10px; background: none; border: none; color: #94a3b8; cursor: pointer;
}
.blog-filter {
  border: 1px solid #E5E7EB; border-radius: 12px; padding: .7rem 1rem; font-size: .88rem; outline: none;
  background: #fff; min-width: 180px;
}
.blog-grid {
  display: grid; gap: 1.25rem;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}
.blog-state {
  text-align: center; padding: 3.5rem 1rem; color: #94a3b8;
}
.blog-state .pi { font-size: 2.6rem; margin-bottom: .8rem; display: block; }
.blog-retry {
  margin-top: .5rem; background: #5B5FEF; color: #fff; border: none;
  border-radius: 10px; padding: .5rem 1.1rem; font-weight: 700; cursor: pointer;
}
.blog-pager {
  display: flex; justify-content: center; gap: .4rem; margin-top: 2rem; flex-wrap: wrap;
}
.blog-pager button {
  min-width: 38px; height: 38px; border: 1px solid #E5E7EB; background: #fff;
  border-radius: 10px; font-weight: 700; color: #374151; cursor: pointer;
}
.blog-pager button.active { background: #5B5FEF; color: #fff; border-color: #5B5FEF; }
.blog-pager button:disabled { opacity: .4; cursor: not-allowed; }
@media (max-width: 768px) {
  .blog-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: .8rem; }
}
</style>
